import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  ButtonSize,
  ButtonState,
  ButtonType,
  IAuth,
  IMaterial,
  InputSize,
  InputState,
  MaterialType,
  UserRole,
} from '../../../assets/types/dtoTypes';
import { BreakpointsService, BIPPERZ_BREAKPOINTS } from '../../shared/services/breakpoint.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import { LoginPageService } from '../../pages/registration-pages/login-page/login-page.service';
import { Subject, takeUntil, timeout } from 'rxjs';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { SocialAuthService } from '../../shared/services/social-auth.service';
import { Router } from '@angular/router';
import { PATH } from '../../../assets/constants';
import {TranslateModule} from "@ngx-translate/core";
import {InputComponent} from "../../shared/input/input.component";
import {MatError} from "@angular/material/select";
import {ButtonComponent} from "../../shared/button/button.component";
import {NgIf} from "@angular/common";
import {SettingsPageService} from "../../pages/main-pages/user/settings-page/settings-page.service";


@Component({
  selector: 'app-login-popup-modal',
  templateUrl: './login-popup-modal.component.html',
  standalone: true,
  styleUrl: './login-popup-modal.component.scss',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    InputComponent,
    MatError,
    ButtonComponent,
    NgIf
  ],
  animations: [
    trigger('popupAnimation', [
      state('void', style({
        opacity: 0,
        transform: 'translateY(-100px)',
      })),
      transition(':enter', [
        animate('0.3s ease-out', style({
          opacity: 1,
          transform: 'translateY(0)',
        })),
      ]),
    ]),
  ]
})
export class LoginPopupModalComponent implements OnInit, OnDestroy {
  private dialogRef = inject<MatDialogRef<LoginPopupModalComponent>>(MatDialogRef);
  private breakpointsService = inject(BreakpointsService);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private loginService = inject(LoginPageService);
  private authenticationService = inject(AuthenticationService);
  private socialAuthService = inject(SocialAuthService);
  private router = inject(Router);
  private settingsPageService = inject(SettingsPageService);

  public isBackendError = false;

  public backendError!: string;

  public isLoading = false;

  destroy$: Subject<boolean> = new Subject<boolean>();

  protected readonly InputSize = InputSize;

  protected readonly InputState = InputState;

  protected readonly ButtonSize = ButtonSize;

  protected readonly ButtonType = ButtonType;

  protected readonly ButtonState = ButtonState;

  protected isMobile: boolean;

  loginForm: FormGroup;

  constructor() {
    this.authenticationService.deleteOldToken();
  }

  get email() {
    return this.loginForm.get( 'email' );
  }

  get password() {
    return this.loginForm.get( 'password' );
  }

  ngOnInit(): void {
    this.breakpointsService.observe( [ BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down ] ).subscribe( ( result ) => {
      this.isMobile = result.s || result.m_down;

    } );
    this.loginForm = this.fb.group( {
      email: [ '', [ Validators.required, Validators.email ] ],
      password: [ '', [ Validators.required, Validators.minLength( 6 ) ] ],
    } );

  }

  onSubmit() {
    if ( this.loginForm.valid ) {
      this.isLoading = true;
      this.loginService.loginUser( this.loginForm.value ).pipe(
        takeUntil( this.destroy$ ),
      ).subscribe( {
        next: ( data: IAuth ) => {
          this.authenticationService.setAccessToken( data.accessToken );
          this.navigateAfterLogin();
        },
        error: error => {
          this.isLoading = false;
          this.backendError = error.error.message;
          this.isBackendError = true;
          this.cdr.detectChanges();
        },
      } );
    }
  }


  private get form(): FormGroup {
    return this.loginForm;
  }

  isInvalidAndTouched( field: string ): boolean {
    const formField = this.form.get( field );
    return formField !== null && !formField.valid && formField.touched;
  }

  public register() {
    this.router.navigate( [ PATH.REGISTER ] );
    this.closeModal();

  }

  public showForgotPassword() {
    this.router.navigate( [ PATH.FORGOT_PASSWORD ] );
    this.closeModal();
  }

  public loginWithGoogle(): void {
    this.socialAuthService.loginWithGoogle();
  }

  public loginWithApple(): void {
    this.isLoading = true;
    this.socialAuthService.loginWithApple(null, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: IAuth) => {
          this.authenticationService.setAccessToken(data.accessToken);
          this.navigateAfterLogin();
        },
        error: () => {
          this.isLoading = false;
          this.isBackendError = true;
        },
      });
  }

  private navigateAfterLogin(): void {
    const id = this.authenticationService.getIdFromAccessToken();
    const companyId = this.authenticationService.getCompanyIdFromAccessToken();
    const role = this.authenticationService.getUserRoleFromAccessToken();

    if (role === UserRole.USER && id) {
      this.settingsPageService.getUser(id).pipe(
        takeUntil(this.destroy$),
        timeout(10000),
      ).subscribe({
        next: (user) => {
          const hasProfileImage = !!(user.profileImageUrl && user.profileImageUrl.trim().length > 0);
          const hasCv = (user.materials ?? []).some((material: IMaterial) => material?.type === MaterialType.CV);
          if (!hasProfileImage) {
            this.router.navigate([PATH.REGISTER], { queryParams: { onboarding: 'photo' } });
            this.closeModal();
            return;
          }
          if (!hasCv) {
            this.router.navigate([PATH.REGISTER], { queryParams: { onboarding: 'cv' } });
            this.closeModal();
            return;
          }
          this.router.navigate([`${PATH.MAIN}/${id}`]);
          this.closeModal();
        },
        error: () => {
          this.router.navigate([`${PATH.MAIN}/${id}`]);
          this.closeModal();
        },
      });
      return;
    }

    if (role === UserRole.USER) {
      this.router.navigate([`${PATH.MAIN}/${id}`]);
      this.closeModal();
    } else if (role === UserRole.PARTNER) {
      this.router.navigate([`${PATH.COMPANY_DASHBOARD}/${companyId}`]);
      this.closeModal();
    } else if (role === UserRole.ADMIN) {
      this.router.navigate([PATH.ADMIN_DASHBOARD]);
      this.closeModal();
    } else {
      this.router.navigate([PATH.AGENCY_DASHBOARD]);
      this.closeModal();
    }
  }

  closeModal() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.destroy$.next( true );
    this.destroy$.unsubscribe();
  }
}
